/**
 * @file FormationSelectorContainer.tsx
 * @description Container for the formation chip-grid selector.
 * Owns the open/close state, groups formations by defensive line count,
 * and wraps the View in a Popover (desktop) or the shared MobileSheetChrome
 * bottom sheet (mobile, SE-5547).
 * @module features/squad-planning/components/FormationSelector
 */

import { ChevronDown } from "lucide-react-native";
import React, { memo, useCallback, useMemo, useState } from "react";

import MobileSheetChrome from "@/components/molecules/MobileSheetChrome";
import { Icon } from "@/components/atoms/Icon";
import {
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from "@/components/atoms/Popover";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { useTheme } from "@/providers/ThemeProvider";

import type {
  FormationOption,
  SquadPlanningFormation,
} from "@/features/squad-planning/types";

import FormationSelectorView, {
  type FormationGroup,
  GROUP_KEYS,
  GROUP_LABELS,
} from "./FormationSelectorView";

/** Props for {@link FormationSelectorContainer}. */
interface Props {
  /** Currently selected formation */
  readonly selectedFormation: SquadPlanningFormation;
  /** Display label for the selected formation (e.g. "4-3-3") */
  readonly selectedFormationLabel: string;
  /** Available formation options */
  readonly formationOptions: readonly FormationOption[];
  /** Called when the user taps a different formation chip */
  readonly onFormationSelect: (value: string) => void;
  /** Whether the viewport is mobile (uses Actionsheet instead of Popover) */
  readonly isMobile?: boolean;
}

/**
 * Container that manages picker state, groups formations, and renders
 * a Popover (desktop) or MobileSheetChrome sheet (mobile) around the chip
 * grid View.
 * @param props - Container props
 * @param props.selectedFormation - The currently active formation
 * @param props.selectedFormationLabel - Display label for the trigger
 * @param props.formationOptions - Flat list of formation options
 * @param props.onFormationSelect - Callback for formation changes
 * @param props.isMobile - Whether the viewport is mobile
 * @returns The formation selector with viewport-appropriate picker
 */
function FormationSelectorContainer({
  selectedFormation,
  selectedFormationLabel,
  formationOptions,
  onFormationSelect,
  isMobile = false,
}: Props) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSelect = useCallback(
    (value: string) => {
      onFormationSelect(value);
      setIsOpen(false);
    },
    [onFormationSelect]
  );

  const groups: readonly FormationGroup[] = useMemo(
    () =>
      GROUP_KEYS.reduce<readonly FormationGroup[]>((acc, key) => {
        const options = formationOptions.filter(o => o.label[0] === key);
        if (options.length === 0) return acc;
        return [
          ...acc,
          { key, label: GROUP_LABELS[key] ?? `${key} at the back`, options },
        ];
      }, []),
    [formationOptions]
  );

  const popoverTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => (
      <Pressable
        {...triggerProps}
        testID="formation-selector-trigger"
        accessibilityRole="button"
        accessibilityLabel={`Formation: ${selectedFormationLabel}. Tap to change.`}
        // SE-8486 (S20, frame 255:7010 `Select · Formation` md): the Select
        // atom's md geometry — 40 tall, `rounded-sm`, the default outline —
        // filling the panel's content width.
        className="h-10 w-full flex-row items-center gap-1 rounded-sm border border-outline-default px-3"
      >
        <Text variant="caption-strong" className="flex-1 text-content-primary">
          {selectedFormationLabel}
        </Text>
        <Text variant="caption" className="text-content-subtle">
          {"\u25BE"}
        </Text>
      </Pressable>
    ),
    [selectedFormationLabel]
  );

  // SE-6622: this was an `isDark` ternary whose two branches were already the
  // SAME style — the dark arm read `border-outline-default bg-surface-base` and
  // the light arm `border-outline-200 bg-background-0`, which are the raw-palette
  // spelling of those very tokens. Unifying the tiers made the duplication
  // visible (sonarjs/no-all-duplicated-branches). Collapsed to the single value
  // both arms already produced, so the render is unchanged. A manual `isDark`
  // fork is redundant here by design: the semantic tokens auto-invert by theme.
  const contentBg = "border-outline-default bg-surface-base";

  const titleColor = isDark ? "text-content-inverse" : "text-content-primary";

  const chipGrid = (
    <FormationSelectorView
      selectedFormation={selectedFormation}
      groups={groups}
      onSelect={handleSelect}
    />
  );

  if (!isMobile) {
    return (
      <Popover
        isOpen={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        trigger={popoverTrigger}
        placement="bottom left"
      >
        <PopoverBackdrop />
        <PopoverContent
          className={`w-auto max-w-96 rounded-md p-4 ${contentBg}`}
        >
          <PopoverBody>
            <Stack className="pb-3">
              <Text variant="title-sm" className={titleColor}>
                Formation
              </Text>
            </Stack>
            {chipGrid}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {/* SE-6544 mobile trigger restyle: rounded-sm corners, 14px medium
          label, and a 16px chevron icon (Figma 40001010:33983). */}
      <Pressable
        onPress={handleOpen}
        testID="formation-selector-trigger"
        accessibilityRole="button"
        accessibilityLabel={`Formation: ${selectedFormationLabel}. Tap to change.`}
        className="h-8 min-w-20 flex-row items-center gap-1 rounded-sm border border-outline-strong px-3"
      >
        <Text variant="body-bold" className="flex-1 text-content-primary">
          {selectedFormationLabel}
        </Text>
        <Icon as={ChevronDown} size="sm" className="text-content-subtle" />
      </Pressable>
      {/* Mobile: the shared MobileSheetChrome owns the sheet surface, drag
          handle, title, and every dismissal path (SE-5547). */}
      <MobileSheetChrome
        isOpen={isOpen}
        onClose={handleClose}
        title="Formation"
      >
        {chipGrid}
      </MobileSheetChrome>
    </>
  );
}

FormationSelectorContainer.displayName = "FormationSelectorContainer";

export default memo(FormationSelectorContainer);
